import { PortalError } from '@app/utils/error/portal-error';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface Result<T> {
  anyError: boolean;
  anySuccess: boolean;
  errors: PortalError[];
  results: T;
}

/**
 * Works like the normal {@link forkJoin}, but it will not fail on
 * errors. Instead, it will collect all errors and emit a result
 * when all sources have either finished or failed.
 */
export function forkJoinRecovering<T>(sources: Observable<T>[]): Observable<Result<T[]>> {
  const results$ = sources.map((source: Observable<any>) =>
    source.pipe(
      map((result) => {
        return {
          error: false,
          result,
        };
      }),
      catchError((error) => {
        return of({
          error: true,
          result: error,
        });
      })
    )
  );

  return forkJoin(results$).pipe(
    map((results) => {
      const failed = Symbol();
      const errors: PortalError[] = [];
      const successfulResults = results
        .map(({ error, result }) => {
          if (error) {
            errors.push(result);
            return failed;
          }

          return result;
        })
        .filter((result) => result != failed);

      return {
        anyError: errors.length > 0,
        anySuccess: successfulResults.length > 0,
        errors,
        results: successfulResults,
      };
    })
  );
}
